import { useApiGateway, useAtsEmployerApi } from '@/composables/useApi';
import type { EmployerProfile } from '@factoryfixinc/ats-interfaces';
import { InternalError } from '../errors/internal.error';

export class EmployerProfilePersistence {
  public async updateEmployerProfile(
    employerId: EmployerProfile['employerId'],
    employerUpdate: Partial<EmployerProfile>,
  ): Promise<void> {
    const url = `/employer-profile/v1/${employerId}`;
    const { error, statusCode } = await useAtsEmployerApi(url).put(employerUpdate);

    if (error?.value) {
      throw new InternalError('Failed to update employer profile data', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }
  }

  public async fetchIsStaffingAgency(employerId: EmployerProfile['employerId']): Promise<boolean> {
    const url = `/job-distribution/v1/company/${employerId}/is-staffing`;
    const { data, error, statusCode } = await useApiGateway(url)
      .get()
      .json<{ isStaffing: boolean }>();

    if (error?.value) {
      throw new InternalError('Failed to fetch staffing eligibility', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }

    return data.value?.isStaffing ?? false;
  }

  public async fetchEmployerProfile(
    employerId: EmployerProfile['employerId'],
  ): Promise<EmployerProfile | undefined> {
    const url = `/employer-profile/v1/${employerId}`;
    const { data, error, statusCode } = await useAtsEmployerApi(url).get().json<EmployerProfile>();

    if (statusCode.value === 404) {
      return undefined;
    }

    if (error?.value) {
      throw new InternalError('Failed to fetch employer profile data', {
        error: error.value,
        status: statusCode.value,
        data: { employerId },
      });
    }

    return data.value ?? undefined;
  }
}
